import { SOPHIE_STORAGE_KEY } from 'store/assistant/consts';

// ── Debug log store ──────────────────────────────────────────────────────────
// Module-level arrays + subscriber pattern (matches original app.js IIFE approach).

export type DebugLogEntry = { ts: string; msg: string };

const logEntries: DebugLogEntry[] = [];
const sttHistory: string[] = [];
let sttBuffer = '';
let nameDetectedFlag = '';
let subscriber: (() => void) | null = null;

const MAX_LOG_ENTRIES = 50;
const MAX_STT_HISTORY = 10;

function notify(): void {
  subscriber?.();
}

export function sophieDebugSubscribe(cb: () => void): () => void {
  subscriber = cb;
  return () => {
    if (subscriber === cb) subscriber = null;
  };
}

// ── Public API (called from VoiceAssistant) ──────────────────────────────────

export function sophieDebugLog(msg: string): void {
  console.log('[Sophie]', msg);
  const ts = new Date().toLocaleTimeString('en-GB', { hour12: false });
  logEntries.unshift({ ts, msg });
  if (logEntries.length > MAX_LOG_ENTRIES) logEntries.pop();
  notify();
}

export function sophieDebugUpdateStt(buffer: string, finalized?: string): void {
  if (finalized !== undefined) {
    sttHistory.push(finalized);
    if (sttHistory.length > MAX_STT_HISTORY) sttHistory.shift();
    sttBuffer = '';
  } else {
    sttBuffer = buffer;
  }
  notify();
}

export function sophieDebugSetNameFlag(value: string): void {
  nameDetectedFlag = value;
  notify();
}

export function sophieDebugClearStorage(): void {
  localStorage.removeItem(SOPHIE_STORAGE_KEY);
  sophieDebugLog('localStorage CLEARED');
}

// ── Read helpers (called from SophieDebugPanel) ──────────────────────────────

export function getDebugLogEntries(): DebugLogEntry[] {
  return logEntries;
}

export function getDebugSttDisplay(): string {
  const histLines = sttHistory.map((s, i) => i + 1 + ': ' + s).join('\n');
  if (sttBuffer) {
    return (histLines ? histLines + '\n' : '') + '▶ ' + sttBuffer;
  }
  return histLines || '—';
}

export function getDebugNameFlag(): string {
  return nameDetectedFlag || '—';
}

export function getDebugStorageDisplay(): string {
  try {
    const raw = localStorage.getItem(SOPHIE_STORAGE_KEY);
    const data = raw ? JSON.parse(raw) : null;
    if (!data) return '(empty)';
    const lines: string[] = [];
    lines.push('userId: ' + (data.userId || '(not set)'));
    lines.push(
      'userName: ' + (data.userName !== undefined ? '"' + data.userName + '"' : '(not set)')
    );
    if (data.language) lines.push('lang: ' + data.language);
    if (data.lastInteraction) {
      lines.push(
        'last: ' + new Date(data.lastInteraction).toLocaleTimeString('en-GB', { hour12: false })
      );
    }
    if (data.conversationHistory) {
      lines.push('history: ' + data.conversationHistory.length + ' msgs');
      data.conversationHistory.slice(-4).forEach((m: { role: string; text?: string }) => {
        const txt = m.text && m.text.length > 200 ? m.text.substring(0, 200) + '…' : m.text || '';
        lines.push('  ' + m.role + ': ' + txt);
      });
    }
    return lines.join('\n');
  } catch (e) {
    return 'Error: ' + (e instanceof Error ? e.message : String(e));
  }
}
